import { ref, computed } from 'vue'

const patronectEnabled = ref(false)
const patronectDeviceIp = ref('')
const patronectDevicePort = ref(8080)
const patronectDeviceId = ref('')
const patronectApiKey = ref('')
const arrivalCooldownMinutes = ref(5)
const isConnected = ref(false)
const lastPingTime = ref(null)
const currentFaces = ref([])
const recentFaces = ref([])
const activeCapture = ref(null)

export function usePatronect() {
  const { savePosSetting, getPosSetting } = usePosDatabase()

  const patronectBaseUrl = computed(() => {
    if (!patronectDeviceIp.value) return null
    return `http://${patronectDeviceIp.value}:${patronectDevicePort.value}`
  })

  const isConfigured = computed(() => {
    return patronectEnabled.value && patronectDeviceIp.value && patronectDevicePort.value
  })

  async function loadSettings() {
    try {
      const enabledVal = await getPosSetting('patronect_enabled')
      patronectEnabled.value = enabledVal === 'true' || enabledVal === true
      patronectDeviceIp.value = await getPosSetting('patronect_device_ip') || ''
      patronectDevicePort.value = parseInt(await getPosSetting('patronect_device_port') || '8080')
      patronectDeviceId.value = await getPosSetting('patronect_device_id') || ''
      patronectApiKey.value = await getPosSetting('patronect_api_key') || ''
      arrivalCooldownMinutes.value = parseInt(await getPosSetting('patronect_arrival_cooldown') || '5')
      console.log('[Patronect] Settings loaded - enabled:', patronectEnabled.value, 'ip:', patronectDeviceIp.value, 'port:', patronectDevicePort.value)
    } catch (err) {
      console.error('[Patronect] Failed to load settings:', err)
    }
  }

  async function saveSettings(settings) {
    try {
      if (settings.enabled !== undefined) {
        patronectEnabled.value = settings.enabled
        await savePosSetting('patronect_enabled', String(settings.enabled))
      }
      if (settings.deviceIp !== undefined) {
        patronectDeviceIp.value = settings.deviceIp
        await savePosSetting('patronect_device_ip', settings.deviceIp)
      }
      if (settings.devicePort !== undefined) {
        patronectDevicePort.value = settings.devicePort
        await savePosSetting('patronect_device_port', String(settings.devicePort))
      }
      if (settings.deviceId !== undefined) {
        patronectDeviceId.value = settings.deviceId
        await savePosSetting('patronect_device_id', settings.deviceId)
      }
      if (settings.apiKey !== undefined) {
        patronectApiKey.value = settings.apiKey
        await savePosSetting('patronect_api_key', settings.apiKey)
      }
      if (settings.arrivalCooldownMinutes !== undefined) {
        arrivalCooldownMinutes.value = settings.arrivalCooldownMinutes
        await savePosSetting('patronect_arrival_cooldown', String(settings.arrivalCooldownMinutes))
      }
      return true
    } catch (err) {
      console.error('[Patronect] Failed to save settings:', err)
      return false
    }
  }

  async function testConnection() {
    if (!patronectBaseUrl.value) {
      return { success: false, error: 'Device IP not configured' }
    }

    try {
      const response = await fetch(`${patronectBaseUrl.value}/api/status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000)
      })

      if (response.ok) {
        const data = await response.json()
        isConnected.value = true
        lastPingTime.value = new Date()
        return { success: true, data }
      } else {
        isConnected.value = false
        return { success: false, error: `HTTP ${response.status}` }
      }
    } catch (err) {
      isConnected.value = false
      return { success: false, error: err.message }
    }
  }

  async function startCapture(orderId, durationSeconds = 10) {
    if (!patronectBaseUrl.value) {
      return { success: false, error: 'Device not configured' }
    }

    try {
      const response = await fetch(`${patronectBaseUrl.value}/api/capture/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          camera_id: 'default',
          order_id: String(orderId),
          duration_seconds: durationSeconds
        }),
        signal: AbortSignal.timeout(5000)
      })

      if (response.ok) {
        const data = await response.json()
        activeCapture.value = {
          captureId: data.capture_id,
          orderId,
          startedAt: new Date()
        }
        return { success: true, captureId: data.capture_id }
      } else {
        return { success: false, error: `HTTP ${response.status}` }
      }
    } catch (err) {
      return { success: false, error: err.message }
    }
  }

  async function getCurrentFaces() {
    if (!patronectBaseUrl.value) return []

    try {
      const response = await fetch(`${patronectBaseUrl.value}/api/faces/current`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000)
      })

      if (response.ok) {
        const data = await response.json()
        currentFaces.value = data.faces || []
        return currentFaces.value
      }
    } catch (err) {
      console.error('[Patronect] Failed to get current faces:', err)
    }
    return []
  }

  async function getRecentFaces(hours = 1) {
    if (!patronectBaseUrl.value) return []

    try {
      const response = await fetch(`${patronectBaseUrl.value}/api/faces/recent?hours=${hours}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000)
      })

      if (response.ok) {
        const data = await response.json()
        recentFaces.value = data.faces || []
        return recentFaces.value
      }
    } catch (err) {
      console.error('[Patronect] Failed to get recent faces:', err)
    }
    return []
  }

  function getThumbnailUrl(filenameOrUuid, bustCache = false) {
    if (!patronectBaseUrl.value || !filenameOrUuid) return null
    const url = `${patronectBaseUrl.value}/api/faces/${filenameOrUuid}/thumbnail`
    return bustCache ? `${url}?t=${Date.now()}` : url
  }

  async function startCaptureViaApi(orderId, deviceId = null, durationSeconds = 10) {
    try {
      const response = await useApi(
        '/api/patronect/start-capture',
        {
          deviceId: deviceId || patronectDeviceId.value,
          orderId: String(orderId),
          durationSeconds,
        },
        { method: 'POST' },
        2
      )
      return { success: true, commandId: response.commandId }
    } catch (err) {
      console.error('[Patronect] API capture failed:', err)
      return { success: false, error: err.message }
    }
  }

  async function getDevices() {
    try {
      const response = await useApi('/api/patronect/devices', {}, { method: 'GET' }, 2)
      return response.devices || []
    } catch (err) {
      console.error('[Patronect] Failed to get devices:', err)
      return []
    }
  }

  async function registerDevice(data) {
    try {
      const response = await useApi('/api/patronect/devices', data, { method: 'POST' }, 2)
      return { success: true, device: response.device }
    } catch (err) {
      console.error('[Patronect] Failed to register device:', err)
      return { success: false, error: err.message }
    }
  }

  async function updateDevice(id, data) {
    try {
      await useApi(`/api/patronect/devices/${id}`, data, { method: 'PUT' }, 2)
      return { success: true }
    } catch (err) {
      console.error('[Patronect] Failed to update device:', err)
      return { success: false, error: err.message }
    }
  }

  async function getStats() {
    try {
      return await useApi('/api/patronect/stats', {}, { method: 'GET' }, 2)
    } catch (err) {
      console.error('[Patronect] Failed to get stats:', err)
      return null
    }
  }

  async function flagAsRunner(faceUuid, orderId = null, notes = null) {
    if (!patronectBaseUrl.value) {
      return { success: false, error: 'Device not configured' }
    }

    try {
      const params = new URLSearchParams()
      if (orderId) params.append('order_id', String(orderId))
      if (notes) params.append('notes', notes)

      const url = `${patronectBaseUrl.value}/api/faces/${faceUuid}/runner${params.toString() ? '?' + params.toString() : ''}`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(10000)
      })

      if (response.ok) {
        const data = await response.json()
        return { success: true, data }
      } else {
        return { success: false, error: `HTTP ${response.status}` }
      }
    } catch (err) {
      console.error('[Patronect] Failed to flag runner:', err)
      return { success: false, error: err.message }
    }
  }

  async function flagAsRunnerWithSimilar(faceUuid, orderId = null, notes = null, threshold = 0.90) {
    if (!patronectBaseUrl.value) {
      return { success: false, error: 'Device not configured' }
    }

    try {
      const params = new URLSearchParams()
      if (orderId) params.append('order_id', String(orderId))
      if (notes) params.append('notes', notes)
      params.append('threshold', String(threshold))

      const url = `${patronectBaseUrl.value}/api/faces/${faceUuid}/runner-with-similar?${params.toString()}`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(10000)
      })

      if (response.ok) {
        const data = await response.json()
        console.log(`[Patronect] Flagged ${data.totalFlagged} similar face(s) as runner`)
        return {
          success: true,
          flaggedFaces: data.flaggedFaces || [],
          totalFlagged: data.totalFlagged || 1
        }
      } else {
        return { success: false, error: `HTTP ${response.status}` }
      }
    } catch (err) {
      console.error('[Patronect] Failed to flag runner with similar:', err)
      return { success: false, error: err.message }
    }
  }

  async function unflagRunner(faceUuid, resolvedBy = null, notes = null) {
    if (!patronectBaseUrl.value) {
      return { success: false, error: 'Device not configured' }
    }

    try {
      const params = new URLSearchParams()
      if (resolvedBy) params.append('resolved_by', resolvedBy)
      if (notes) params.append('notes', notes)

      const url = `${patronectBaseUrl.value}/api/faces/${faceUuid}/unflag${params.toString() ? '?' + params.toString() : ''}`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(10000)
      })

      if (response.ok) {
        const data = await response.json()
        return { success: true, data }
      } else {
        return { success: false, error: `HTTP ${response.status}` }
      }
    } catch (err) {
      console.error('[Patronect] Failed to unflag runner:', err)
      return { success: false, error: err.message }
    }
  }

  async function unflagRunnerWithSimilar(faceUuid, resolvedBy = null, notes = null, threshold = 0.90) {
    if (!patronectBaseUrl.value) {
      return { success: false, error: 'Device not configured' }
    }

    try {
      const params = new URLSearchParams()
      if (resolvedBy) params.append('resolved_by', resolvedBy)
      if (notes) params.append('notes', notes)
      params.append('threshold', String(threshold))

      const url = `${patronectBaseUrl.value}/api/faces/${faceUuid}/unflag-with-similar?${params.toString()}`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(10000)
      })

      if (response.ok) {
        const data = await response.json()
        console.log(`[Patronect] Unflagged ${data.totalUnflagged} similar face(s)`)
        return {
          success: true,
          unflaggedUuids: data.unflaggedUuids || [],
          totalUnflagged: data.totalUnflagged || 1
        }
      } else {
        return { success: false, error: `HTTP ${response.status}` }
      }
    } catch (err) {
      console.error('[Patronect] Failed to unflag runner with similar:', err)
      return { success: false, error: err.message }
    }
  }

  async function getRunnerFaces() {
    if (!patronectBaseUrl.value) return []

    try {
      const response = await fetch(`${patronectBaseUrl.value}/api/faces/runners`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000)
      })

      if (response.ok) {
        const data = await response.json()
        return data.runners || []
      }
    } catch (err) {
      console.error('[Patronect] Failed to get runner faces:', err)
    }
    return []
  }

  async function linkFaceWithSimilar(faceUuid, customerId, threshold = 0.90) {
    if (!patronectBaseUrl.value) {
      return { success: false, error: 'Device not configured' }
    }

    try {
      const params = new URLSearchParams()
      params.append('customer_id', String(customerId))
      params.append('threshold', String(threshold))

      const url = `${patronectBaseUrl.value}/api/faces/${faceUuid}/link-customer-with-similar?${params.toString()}`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(10000)
      })

      if (response.ok) {
        const data = await response.json()
        console.log(`[Patronect] Linked ${data.totalLinked} face(s) to customer ${customerId}`)
        return {
          success: true,
          linkedUuids: data.linkedUuids || [faceUuid],
          totalLinked: data.totalLinked || 1
        }
      } else {
        return { success: false, error: `HTTP ${response.status}` }
      }
    } catch (err) {
      console.error('[Patronect] Failed to link face with similar:', err)
      return { success: false, error: err.message }
    }
  }

  async function findCustomerByFace(faceUuid, threshold = 0.90) {
    if (!patronectBaseUrl.value) {
      return { found: false }
    }

    try {
      const params = new URLSearchParams()
      params.append('threshold', String(threshold))

      const url = `${patronectBaseUrl.value}/api/faces/${faceUuid}/find-customer?${params.toString()}`
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000)
      })

      if (response.ok) {
        return await response.json()
      }
    } catch (err) {
      console.error('[Patronect] Failed to find customer by face:', err)
    }
    return { found: false }
  }

  return {
    patronectEnabled,
    patronectDeviceIp,
    patronectDevicePort,
    patronectDeviceId,
    patronectApiKey,
    arrivalCooldownMinutes,
    patronectBaseUrl,
    isConfigured,
    isConnected,
    lastPingTime,
    currentFaces,
    recentFaces,
    activeCapture,
    loadSettings,
    saveSettings,
    testConnection,
    startCapture,
    getCurrentFaces,
    getRecentFaces,
    getThumbnailUrl,
    startCaptureViaApi,
    getDevices,
    registerDevice,
    updateDevice,
    getStats,
    flagAsRunner,
    flagAsRunnerWithSimilar,
    unflagRunner,
    unflagRunnerWithSimilar,
    getRunnerFaces,
    linkFaceWithSimilar,
    findCustomerByFace
  }
}
